import React, { useState } from "react";
import {useNavigate, useOutletContext} from 'react-router-dom';
import {useMutation} from '@apollo/client';
import WebsitesForm, { confirmNewWebsites } from "../../components/forms/Campaign/WebsitesForm";
import {UPDATE_CAMPAIGN} from '../../utils/api/campaign';
import { CampaignType, UpdatePublicationInput, Website } from "../../__generated__/graphql";
import { useCurrentUser } from "../../utils/CurrentUserContext";
import { trackEvent } from "../../utils/analytics";
import {useTeaserExtensionPrompt} from "../../components/forms/Campaign/useTeaserExtensionPrompt";

const SelectPublishers = ({isNativeDisplay}: {isNativeDisplay: boolean}) => {
  const navigate = useNavigate();
  const {user} = useCurrentUser();
  const {
    campaign, setCampaign,
    campaignDetails: [campaignDetails],
    selectedWebsiteIds: [selectedWebsiteIds, setSelectedWebsiteIds],
    deal,
  } = useOutletContext<any>();

  const [updateCampaign, { loading: saving }] = useMutation(UPDATE_CAMPAIGN);
  const [selectedWebsites, setSelectedWebsites] = useState<Website[]>([])
  const {promptTeaserExtension, teaserExtensionModal} = useTeaserExtensionPrompt()

  const handleNextClick = () => {
    const getBudget = (index: number) => {
      if (campaign.type !== CampaignType.Standard) { 
        return {}
      }

      return {
        budget: Math.floor(campaignDetails.targetValue / selectedWebsiteIds.length)
      + (index === selectedWebsiteIds.length - 1 ? campaignDetails.targetValue % selectedWebsiteIds.length : 0)
      }
    }
    const update = async () => {
      const publications: UpdatePublicationInput[] = selectedWebsiteIds.map((websiteId: string, index: number) => ({
        websiteId,
        ...getBudget(index)
      }));

      const res = await updateCampaign({
        variables: { id: campaign._id, record: { publications } }
      })

      setCampaign(res.data.campaign)

      const currentWebsiteIds = campaign?.publications?.map(({websiteId}: any) => websiteId) || []
      const newWebsiteIds = selectedWebsiteIds.filter((id: string) => !currentWebsiteIds.includes(id))
      await promptTeaserExtension({
        campaignId: campaign._id,
        currentWebsiteIds,
        newWebsiteIds,
      })

      trackEvent('campaign_websites_selected', {
        campaignId: campaign._id,
        campaignType: campaign.type,
        websiteCount: selectedWebsiteIds.length,
        isNativeDisplay,
        step: 2,
        stepName: 'websites',
      });

      // If this is the final step for native display, track campaign creation complete
      if (isNativeDisplay) {
        trackEvent('campaign_created', {
          campaignId: campaign._id,
          campaignType: campaign.type,
          websiteCount: selectedWebsiteIds.length,
          totalSteps: 2,
          budget: campaignDetails.targetValue || null,
        });
      }

      navigate(isNativeDisplay ? `/campaigns/${campaign._id}/websites` : "/create/campaign/create-meta-article");
    }

    confirmNewWebsites(user, selectedWebsites, campaignDetails, update)

  };

  return (
    <>
      <WebsitesForm
        selectedWebsites={selectedWebsiteIds}
        onChange={(newSelectedWebsiteIds, newSelectedWebsites) => {
          setSelectedWebsiteIds(newSelectedWebsiteIds)
          setSelectedWebsites(newSelectedWebsites)
        }}
        filters={{marketplace: campaign.marketplace, campaignType: campaign.type, contentSource: campaign.contentSource, ...(deal?.websiteIds ? {websiteIds: deal.websiteIds} : {})}}
        submitButton={
          <button
            onClick={handleNextClick}
            disabled={selectedWebsiteIds.length === 0}
            data-test-id='submitButton'
            className='py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75'
          >
            Next
          </button>
        }
      />
      {teaserExtensionModal}
    </>
  )
};

export default SelectPublishers;
